import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { openDrawer } from "../../actions/drawerActions";
import { deleteIntegration } from "../../actions/integrationActions";
import { getTemplateList, updatePageAndGetList, updateSortAndGetList } from "../../actions/templateListActions";
import {setIntegrationName} from "../../actions/integrationActions";
import IntegrationCreateEditDrawerHook, { integrationCreateEditDrawerProps } from "../drawers/IntegrationCreateEditDrawerHook";
import IntegrationReviewDrawerHook, { integrationReviewDrawerProps } from "../drawers/IntegrationReviewDrawerHook";
import ArgoTemplateListCreateFabHook from "../common/ArgoTemplateListCreateFabHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook";
import ConfirmPromptHook from "../dialogs/ConfirmPromptHook";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import { QD_URL } from "../../constants/systemConstants";
import formatCopyText from "../../utilities/formatCopyText";


function IntegrationListHook(props) {
    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const [deleteName, setDeleteName] = useState("");
    const [deleteId, setDeleteId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [closePopUp, setClosePopUp] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const integrationList = useSelector(state => state.integrationList);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.all.integration] || {});

    useEffect(() => {
        setLoading(true);
        dispatch(getTemplateList(filterState, TEMPLATE.TYPE.INTEGRATION, TEMPLATE.MODE.ALL));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, JSON.stringify(filterState)]);

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        }
        else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, integrationList]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setLoading(true);
            setFilter(false);
            callback();
        } else {
            setLoading(true);
            setFilter(false);
            callback(parm1);
        }
    };

    const openCreate = () => {
        dispatch(openDrawer(integrationCreateEditDrawerProps, <IntegrationCreateEditDrawerHook createMode={true} setLoadingState={setLoadingStateTrue} />));
    };

    const openEdit = (placeHolder, id) => {
        dispatch(openDrawer(integrationCreateEditDrawerProps, <IntegrationCreateEditDrawerHook id={id} filter={filterState} setLoadingState={setLoadingStateTrue} />)); // placeHolder is just a spacer param
    };

    const openLogs = (placeHolder, id, name, row, event) => {
        dispatch(setIntegrationName(name));
        let path = QD_URL + "/integration-logs/" + id;
        navigate(path, { replace: true });
    };

    const openPreview = (placeHolder, id, name, row, event) => {
        // This will open preview drawer
        formatCopyText(row, event, "Integration:");
        dispatch(openDrawer(integrationReviewDrawerProps, <IntegrationReviewDrawerHook id={id} />)); // placeHolder is just a spacer param
    };

    const onPageChange = (page, size, event) => {
        setLoading(true);
        setFilter(true);
        dispatch(updatePageAndGetList(page, size, filterState, TEMPLATE.TYPE.INTEGRATION, TEMPLATE.MODE.ALL, event));
    };

    const onSortChange = (sort, direction) => {
        setLoading(true);
        setFilter(true);
        dispatch(updateSortAndGetList(sort, direction, filterState, TEMPLATE.TYPE.INTEGRATION, TEMPLATE.MODE.ALL));
    };

    const handleDelete = (id) => {
        let name = "";
        for (let i = 0; i < integrationList.length; i++) {
            if (integrationList[i].id === id) {
                name = integrationList[i].name;
                break;
            }
        }
        setDeleteConfirmationDialogOpen(true);
        setDeleteName(name);
        setDeleteId(id);
    };

    const handleCloseDeleteConfirmationDialog = () => {
        setDeleteConfirmationDialogOpen(false);
        setDeleteName("");
        setDeleteId(null);
    };

    const handleDeleteConfirmed = () => {
        dispatch(deleteIntegration(deleteId, filterState));
        handleCloseDeleteConfirmationDialog();
    };

    const handleClosePopUp = () => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setClosePopUp(true);
    };

    // Add custom actions to row actions
    const customActions = [
        {
            func: openPreview,
            label: "Review"
        },
        {
            func: openEdit,
            label: "Edit"
        },
        {
            func: handleDelete,
            label: "Delete"
        }
    ];

    return (
        <div id="IntegrationList-container-div" style={{ height: "100%", padding: "15px", marginTop: '64px' }}>
            {closePopUp ? "" : <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp} />}

            <ArgoListTitleAndLoadingAnimationHook fileName="IntegrationList" listTitle="Integrations" loading={loading} showOutstandingAlert={false} filter={filter} />

            <div style={{ clear: "both" }} />

            <Card id="IntegrationList-card" variant="outlined" sx={{ border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke", overflow: "auto" }}>

                <CardContent id="IntegrationList-card-content">

                    <TableViewHook
                        templateType={TEMPLATE.TYPE.INTEGRATION}
                        templateMode={TEMPLATE.MODE.ALL}
                        setLoadingState={setLoadingStateTrue}
                        // filter={<FilterPopOverHook page="integrations"
                        //                        templateType={TEMPLATE.TYPE.INTEGRATION}
                        //                        templateMode={TEMPLATE.MODE.ALL}
                        //                        filterName={TEMPLATE.FILTER.all.integration}
                        //                        submitValue={getTemplateList}
                        //                        integrationTypes={IntegrationTypesFilter}
                        //                        showIntegrationTypes={true}
                        //                        showQualifier={false}
                        //                        setLoadingState={setLoadingStateTrue}
                        //
                        // />}
                    >
                        <TableGridHook
                            id="IntegrationsList-table-grid"
                            displayType={"integrations"}
                            dataSource={integrationList}
                            nameDrawer={openLogs}
                            customActions={customActions}
                            pageState={filterState}
                            onPageChange={onPageChange}
                            sortState={filterState}
                            onSortChange={onSortChange}
                            templateType={TEMPLATE.TYPE.INTEGRATION}
                            templateMode={TEMPLATE.MODE.ALL}
                            showQualifier={false}
                            showStatus={false}
                            showVersionCount={false}
                            showIntegrationType={true}
                            showGridActionVersions={false}
                            uniqueIdentifier={"id"}
                        />
                    </TableViewHook>

                    <ArgoTemplateListCreateFabHook
                        fileName="IntegrationList"
                        fieldName="fab"
                        hoverText="Create Integration"
                        onClick={openCreate}
                    />

                    <ConfirmPromptHook
                        open={deleteConfirmationDialogOpen}
                        title="Delete Integration"
                        message={"Are you sure you want to delete the integration named: " + deleteName + "?"}
                        ok={handleDeleteConfirmed}
                        cancel={handleCloseDeleteConfirmationDialog}
                    />

                </CardContent>

            </Card>

        </div>
    );
}

IntegrationListHook.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
};

export default IntegrationListHook;